/**
 * NaplanovatModal — Phase 4.4 plan quick-add / edit dialog.
 *
 * Used from
 * KontaktDetail's "Naplánovat" button, KontaktyList's Inspector "Naplánovat"
 * action, and PlanovaneList's "Nový plán" / row-edit affordances.
 *
 * The form is intentionally tiny — datum, optional čas, prodejce, popis. Date input uses
 * the native HTML5 picker styled with the bottom-border idiom (matches the
 * design's "Date picker bottom-border style" note). Save toasts on success.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { DialogCrown, Modal } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Textarea } from '@/components/Textarea';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { maskTimeInput, normalizeTimeInput, toTimeInputValue, todayInputValue } from '@/modules/ukoly/helpers';
import {
  useCompletePlanMutation,
  useContactSellersQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  type ContactPlan,
} from './api';

export interface NaplanovatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: number;
  /** Optional context label — usually the kontakt's display name. */
  contactLabel?: string;
  /** When set, the modal opens in edit mode pre-filled with this plan. */
  plan?: Pick<ContactPlan, 'id_planu' | 'datum_planu' | 'popis' | 'id_prodejce'> & Partial<Pick<ContactPlan, 'cas_planu'>>;
  /** Default seller for create mode (typically the current user). */
  defaultSellerId?: number;
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function NaplanovatModal({
  open,
  onOpenChange,
  contactId,
  contactLabel,
  plan,
  defaultSellerId,
}: NaplanovatModalProps) {
  const isEdit = plan != null;
  const sellersQuery = useContactSellersQuery();
  const sellers = sellersQuery.data?.sellers ?? [];

  const [datum, setDatum] = useState('');
  // Optional 24h time-of-day (HH:MM text — see maskTimeInput); empty = whole day.
  const [cas, setCas] = useState('');
  const [sellerId, setSellerId] = useState<string>('');
  const [popis, setPopis] = useState('');

  // Reset form whenever the modal opens or the input plan changes.
  useEffect(() => {
    if (!open) return;
    if (isEdit && plan) {
      setDatum(toDateInputValue(plan.datum_planu));
      setCas(toTimeInputValue(plan.cas_planu));
      setSellerId(plan.id_prodejce ? String(plan.id_prodejce) : '');
      setPopis(plan.popis ?? '');
    } else {
      // Default to today; create mode picks current user when defaultSellerId set.
      setDatum(todayInputValue());
      setCas('');
      setSellerId(defaultSellerId ? String(defaultSellerId) : '');
      setPopis('');
    }
  }, [open, isEdit, plan, defaultSellerId]);

  const createMutation = useCreatePlanMutation(contactId);
  const updateMutation = useUpdatePlanMutation(contactId, plan?.id_planu ?? 0);
  const completeMutation = useCompletePlanMutation();

  const isPending =
    createMutation.isPending || updateMutation.isPending || completeMutation.isPending;

  const canSubmit = useMemo(() => datum.trim().length === 10 && !isPending, [datum, isPending]);

  async function handleSubmit() {
    if (!canSubmit) return;
    const casNorm = normalizeTimeInput(cas);
    try {
      if (isEdit && plan) {
        await updateMutation.mutateAsync({
          datum_planu: datum,
          cas_planu: casNorm || undefined,
          popis: popis.trim() || undefined,
        });
      } else {
        await createMutation.mutateAsync({
          datum_planu: datum,
          cas_planu: casNorm || undefined,
          ...(sellerId ? { id_prodejce: Number(sellerId) } : {}),
          popis: popis.trim() || undefined,
        });
      }
      toast.ok(isEdit ? 'Plán upraven' : 'Plán uložen');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Plán se nepodařilo uložit';
      toast.error(message);
    }
  }

  async function handleComplete() {
    if (!isEdit || !plan || isPending) return;
    try {
      await completeMutation.mutateAsync({ contactId, planId: plan.id_planu });
      toast.ok('Plán splněn');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Nepodařilo se splnit plán';
      toast.error(message);
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={560}>
      <DialogCrown
        tone="neutral"
        eyebrow={isEdit ? 'Upravit plán' : 'Naplánovat kontakt'}
        eyebrowMeta={contactLabel ? <>№ {contactId} · {contactLabel}</> : <>№ {contactId}</>}
        title={
          isEdit ? (
            <>
              Upravit <em>plánovaný kontakt</em>
            </>
          ) : (
            <>
              Naplánuj <em>další krok</em>
            </>
          )
        }
        sub={
          isEdit ? (
            <>Změnit datum, čas nebo popis plánovaného kontaktu.</>
          ) : (
            <>Datum, volitelný čas, prodejce a krátký popis. Plán se objeví na detailu kontaktu i v sekci Plánované.</>
          )
        }
      />
      <Modal.Body>
        <Modal.Section>
          <Fields cols={2}>
            <Field label="Datum plánu" required>
              <TextInput
                type="date"
                value={datum}
                onChange={(e) => setDatum(e.currentTarget.value)}
                data-testid="naplanovat-datum"
              />
            </Field>
            <Field label="Čas" optional="nepovinné · jinak celý den">
              <TextInput
                inputMode="numeric"
                placeholder="HH:MM"
                maxLength={5}
                value={cas}
                onChange={(e) => setCas(maskTimeInput(e.currentTarget.value))}
                onBlur={() => setCas((v) => normalizeTimeInput(v))}
                data-testid="naplanovat-cas"
              />
            </Field>
            <Field label="Prodejce" optional={isEdit ? 'beze změny' : 'výchozí: ty'} span>
              <SelectInput
                value={sellerId}
                onChange={(e) => setSellerId(e.currentTarget.value)}
                disabled={isEdit}
                data-testid="naplanovat-seller"
              >
                <option value="">{isEdit ? '— beze změny —' : '— výchozí —'}</option>
                {sellers.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {[s.jmeno, s.prijmeni].filter(Boolean).join(' ')}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Popis" optional="krátká poznámka, co se má stát" span>
              <Textarea
                rows={4}
                value={popis}
                onChange={(e) => setPopis(e.currentTarget.value)}
                placeholder="např. Telefonát ohledně objednávky"
                data-testid="naplanovat-popis"
              />
            </Field>
          </Fields>
        </Modal.Section>
      </Modal.Body>
      <Modal.Foot
        meta={<>Esc — zavřít · ⏎ — uložit</>}
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              mono
              onClick={() => onOpenChange(false)}
            >
              Zrušit
            </Button>
            {isEdit && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                mono
                disabled={isPending}
                onClick={handleComplete}
                data-testid="naplanovat-complete"
              >
                {completeMutation.isPending ? 'Plním…' : 'Splnit'}
              </Button>
            )}
            <Button
              type="button"
              variant="ink"
              size="sm"
              mono
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid="naplanovat-save"
            >
              {updateMutation.isPending || createMutation.isPending
                ? 'Ukládám…'
                : isEdit
                  ? 'Uložit změny'
                  : 'Uložit plán'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
